import axios from 'axios';

// Backend API configuration
const BACKEND_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'https://thccdz7694.execute-api.us-east-1.amazonaws.com/prod';

// Create axios instance for backend calls
const backendApi = axios.create({
  baseURL: BACKEND_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  }
});

// Add request interceptor for debugging
backendApi.interceptors.request.use(
  config => {
    console.log('Backend Request:', {
      url: config.url,
      method: config.method,
      data: config.data
    });
    return config;
  },
  error => {
    console.error('Backend Request Error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor for debugging
backendApi.interceptors.response.use(
  response => {
    console.log('Backend Response:', {
      url: response.config.url,
      status: response.status,
      data: response.data
    });
    return response;
  },
  error => {
    console.error('Backend API Error:', {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      data: error.response?.data,
      message: error.message
    });
    return Promise.reject(error);
  }
);

export const searchUsersByEmail = async (email) => {
  try {
    console.log('Searching for users with email:', email);
    
    const response = await backendApi.post('/search-users', { email });
    const users = response.data.users;
    
    console.log('Found users:', users);
    return users;
  } catch (error) {
    console.error('Search error:', error.response?.data || error.message);
    
    // Preserve the original error structure for better frontend handling
    if (error.response) {
      const enhancedError = new Error(`Failed to search for users: ${error.response.data?.error || error.message}`);
      enhancedError.response = error.response;
      throw enhancedError;
    }
    
    throw new Error(`Failed to search for users: ${error.message}`);
  }
};

export const activateUser = async (email) => {
  try {
    console.log('Activating user:', email);
    
    const response = await backendApi.post('/activate-user', { email });
    
    console.log('Activation result:', response.data);
    return response.data;
  } catch (error) {
    console.error('Activation error:', error.response?.data || error.message);
    
    // Preserve the original error structure for better frontend handling
    if (error.response) {
      // Create a new error that preserves the response structure
      const enhancedError = new Error(`Failed to activate user: ${error.response.data?.error || error.message}`);
      enhancedError.response = error.response;
      throw enhancedError;
    }
    
    throw new Error(`Failed to activate user: ${error.message}`);
  }
};

export const deactivateAllUsers = async () => {
  try {
    console.log('Deactivating all eligible users...');
    
    const response = await backendApi.post('/deactivate-all-users');
    
    console.log('Deactivation result:', response.data);
    return response.data;
  } catch (error) {
    console.error('Deactivation error:', error.response?.data || error.message);
    
    // Preserve the original error structure for better frontend handling
    if (error.response) {
      const enhancedError = new Error(`Failed to deactivate users: ${error.response.data?.error || error.message}`);
      enhancedError.response = error.response;
      throw enhancedError;
    }
    
    throw new Error(`Failed to deactivate users: ${error.message}`);
  }
};

export const exportUsersToCSV = async () => {
  try {
    console.log('Exporting users to CSV...');
    
    const response = await backendApi.get('/export-users', {
      responseType: 'blob' // Important for CSV download
    });
    
    // Create blob and download file
    const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `alchemer_users_export_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Parse content-disposition header to get the count
    console.log('CSV export completed');
    
    return { success: true, message: 'Users exported successfully' };
  } catch (error) {
    console.error('Export error:', error.response?.data || error.message);
    
    // Preserve the original error structure for better frontend handling
    if (error.response) {
      const enhancedError = new Error(`Failed to export users: ${error.response.data?.error || error.message}`);
      enhancedError.response = error.response;
      throw enhancedError;
    }
    
    throw new Error(`Failed to export users: ${error.message}`);
  }
};

export const getUserStatus = async (alchemerId) => {
  try {
    console.log('Getting user status for:', alchemerId);
    
    const response = await backendApi.get(`/user-status/${alchemerId}`);
    
    console.log('User status:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error fetching user status:', error.response?.data || error.message);
    return {
      status: 'Error',
      userstatus: 'Error',
      last_login: 'Error'
    };
  }
};

// Helper function to test backend connectivity
export const testBackendConnection = async () => {
  try {
    await backendApi.get('/');
    return { success: true, message: 'Backend connected successfully' };
  } catch (error) {
    console.error('Backend connection test failed:', error);
    return { success: false, message: `Backend connection failed: ${error.message}` };
  }
}; 